import { useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useNextQueryParams } from '@hooks/useNextQueryParams';
import { getProductIdForLocale } from '@libs/product.helper';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { getLocale } from '@libs/constants';

export const useProductPageUrl = () => {
  const router = useRouter();
  const locale = getLocale(router);
  const query = useNextQueryParams();
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const defaultProduct = useProductPageStore(state => state.getProductWithLowestPrice());
  const defaultVariantSku = getProductIdForLocale(defaultProduct, locale);

  const cartPositionIdParam: string = query.i as string; // the cartPositionId provided by the shopping cart to retrieve cart position data
  const editParam: string = query.edit as string; // edit flag for echain product page

  const updateUrl = useCallback(
    async (sku: string | undefined) => {
      try {
        const queryArtnr: { i?: string; edit?: string; artnr?: string } = {
          ...(cartPositionIdParam && editParam ? { i: cartPositionIdParam, edit: editParam } : {}),
        };
        if (sku) {
          queryArtnr.artnr = sku;
        }
        await router.replace(
          {
            pathname: router.asPath.split('?')[0],
            query: queryArtnr,
          },
          undefined,
          { shallow: true }
        );
      } catch (error) {
        Sentry.captureException(
          new Error(
            `Could replace router pathname ${
              router.asPath.split('?')[0]
            } and query with newVariant id  - ${JSON.stringify(error)}`
          )
        );
      }
    },
    [router, cartPositionIdParam, editParam]
  );

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const productIdDisplayName = getProductIdForLocale(currentProduct, locale);
    if (defaultVariantSku) {
      if (productIdDisplayName !== defaultVariantSku) {
        void updateUrl(productIdDisplayName);
      } else {
        void updateUrl(null);
      }
    }
  }, [currentProduct, locale]);
};
